/**
 * Edits, as world → world. Each keeps the one invariant a mount promises: an
 * 'axis' part sits at y = 0 facing along the rail. Snapping to a grid is the
 * surface's business; these take whatever numbers they are given.
 */
import { defOf, kindOf } from './registry';
import { Mount, Part, Vec2, World } from './types';

/** The rail runs at least this far either side of its zero mark: 2 m in all,
 *  the view at 100%. */
export const RAIL_HALF = 100;
/** No part stands further than this from the zero mark, either way. */
export const RAIL_LIMIT = 2000;
/** How far past the outermost part the rail runs. */
const RAIL_MARGIN = 30;

const withinRail = (x: number): number => Math.min(RAIL_LIMIT, Math.max(-RAIL_LIMIT, x));

export function emptyWorld(): World {
    return {
        parts: [],
        medium: 1,
        axis: { from: -RAIL_HALF, to: RAIL_HALF },
        light: { wavelengths: [589] },
    };
}

/** The rail is long enough, either way, for every part, within its bounds. */
export function fitRail(w: World): World {
    const xs = w.parts.map(p => p.at.x);
    const from = withinRail(Math.min(-RAIL_HALF, ...xs.map(x => x - RAIL_MARGIN)));
    const to = withinRail(Math.max(RAIL_HALF, ...xs.map(x => x + RAIL_MARGIN)));
    return from === w.axis.from && to === w.axis.to ? w : { ...w, axis: { from, to } };
}

export function mintId(w: World, key: string): string {
    let n = w.parts.length + 1;
    while (w.parts.some(p => p.id === `${key}-${n}`)) n++;
    return `${key}-${n}`;
}

/** 0 or π, whichever is nearer: the two ways a part on the rail can face. */
const alongRail = (angle: number): number => (Math.cos(angle) >= 0 ? 0 : Math.PI);

const onRail = (p: Part): Part => ({ ...p, at: { x: withinRail(p.at.x), y: 0 }, angle: alongRail(p.angle) });

/** Put a new part on the bench — by default at the zero mark, or 20 cm right of the rightmost part. */
export function place(w: World, key: string, at?: Vec2): { world: World; id: string } {
    const kind = kindOf(key);
    const id = mintId(w, key);
    const right = w.parts.length ? Math.max(...w.parts.map(p => p.at.x)) + 20 : 0;
    const created = kind.create(at ?? { x: right, y: 0 }, id) as unknown as Part;
    const part = created.mount === 'axis' ? onRail(created) : created;
    return { world: fitRail({ ...w, parts: [...w.parts, part] }), id };
}

const update = (w: World, id: string, f: (p: Part) => Part): World =>
    fitRail({ ...w, parts: w.parts.map(p => (p.id === id ? f(p) : p)) });

export const movePart = (w: World, id: string, to: Vec2): World =>
    update(w, id, p => (p.mount === 'axis' ? onRail({ ...p, at: to }) : { ...p, at: { x: withinRail(to.x), y: to.y } }));

export const turnPart = (w: World, id: string, angle: number): World =>
    update(w, id, p => {
        if (p.mount === 'axis') return { ...p, angle: alongRail(angle) };
        return defOf(p).freedom.turns ? { ...p, angle } : p;
    });

/** Lift a part off the rail, or put it back. Only kinds that may sit on the rail can. */
export const setMount = (w: World, id: string, mount: Mount): World =>
    update(w, id, p => {
        if (mount === 'axis' && !defOf(p).freedom.onAxis) return p;
        return mount === 'axis' ? onRail({ ...p, mount }) : { ...p, mount };
    });

/** Re-derive every part's worked-out fields, after anything they depend on changed. */
export function rederive(w: World): World {
    return {
        ...w,
        parts: w.parts.map(p => {
            const derived = defOf(p).derive?.(p, w);
            return derived ? { ...p, data: { ...p.data, ...derived } } : p;
        }),
    };
}

export const setData = (w: World, id: string, patch: Record<string, unknown>): World =>
    rederive(update(w, id, p => ({ ...p, data: { ...p.data, ...patch } })));

export const setLabel = (w: World, id: string, label: string | undefined): World =>
    update(w, id, p => {
        const { label: _old, ...rest } = p;
        return label ? { ...rest, label } : rest;
    });

export const removePart = (w: World, id: string): World =>
    fitRail({ ...w, parts: w.parts.filter(p => p.id !== id) });

export const setMedium = (w: World, medium: number): World => rederive({ ...w, medium });
export const setLight = (w: World, light: Partial<World['light']>): World => ({ ...w, light: { ...w.light, ...light } });
